/**
 * Config-driven status badges: a raw VEOS status value (`STATUT`, `ETAT`,
 * `QUI_SITUATION`, `VEH_SITUATION`, …) is mapped to a display label and a
 * design-system `Badge` variant through a declarative {@link StatusBadgeConfig}
 * — never through inline JSX conditions.
 */

import type { BadgeVariant } from '@components/base/display';

/** How one status value renders. */
export interface StatusBadgeEntry {
  /** Display label; omitted ⇒ the raw value (trimmed) is shown. */
  label?: string;
  variant: BadgeVariant;
  /** Leading dot (the design's « ● En cours » list status). */
  dot?: boolean;
}

/** Declarative value → badge map. */
export interface StatusBadgeConfig {
  /**
   * Entries keyed by status value. Keys and values are compared normalised
   * (trimmed, upper-cased, accents stripped), so `'Payée'` matches
   * `"PAYEE"` and `" payée "`.
   */
  entries: Readonly<Record<string, StatusBadgeEntry>>;
  /**
   * `'exact'` (default) or `'prefix'`: with `'prefix'` a value also matches
   * the first entry whose key it starts with (e.g. `"Payée client"` →
   * `Payée`). Exact matches always win.
   */
  match?: 'exact' | 'prefix';
  /** Variant of a value missing from `entries` (shown verbatim). Default `'default'`. */
  fallbackVariant?: BadgeVariant;
  /** Default `dot` for every entry (an entry's own `dot` wins). */
  dot?: boolean;
}

/** The badge a value resolves to. */
export interface ResolvedStatusBadge {
  label: string;
  variant: BadgeVariant;
  dot: boolean;
}

/** Normalise a status value for matching: trim, strip accents, upper-case. */
export function normaliseStatus(value: string): string {
  return value
    .trim()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase();
}

/**
 * The `entries` key a raw value matches (exact first, then by prefix when
 * `config.match === 'prefix'`), or `undefined`. `null` / blank ⇒ `undefined`.
 */
export function matchStatusEntryKey(
  value: string | number | null | undefined,
  config: StatusBadgeConfig,
): string | undefined {
  if (value === null || value === undefined) return undefined;
  const normalised = normaliseStatus(String(value));
  if (normalised === '') return undefined;

  const keys = Object.keys(config.entries);
  const exact = keys.find((key) => normaliseStatus(key) === normalised);
  if (exact !== undefined || config.match !== 'prefix') return exact;
  return keys.find((key) => normalised.startsWith(normaliseStatus(key)));
}

/**
 * Resolve a raw status value against a config. `null` / blank ⇒ `null`
 * (nothing rendered — the VEOS "null renders nothing" rule).
 */
export function resolveStatusBadge(
  value: string | number | null | undefined,
  config: StatusBadgeConfig,
): ResolvedStatusBadge | null {
  if (value === null || value === undefined) return null;
  const raw = String(value).trim();
  if (raw === '') return null;

  const matchedKey = matchStatusEntryKey(raw, config);
  const entry = matchedKey === undefined ? undefined : config.entries[matchedKey];

  return {
    label: entry?.label ?? raw,
    variant: entry?.variant ?? config.fallbackVariant ?? 'default',
    dot: entry?.dot ?? config.dot ?? false,
  };
}

/** Pièce status (`STATUT`): « EN ATTENTE » → orange « En attente » (fiches 22721 / 22746 / 22780). */
export const PIECE_STATUS_BADGE: StatusBadgeConfig = {
  entries: {
    'EN ATTENTE': { label: 'En attente', variant: 'warning' },
  },
  fallbackVariant: 'default',
};

/**
 * Premium situation (`QUI_SITUATION`, fiches 22702 / 22749): the raw value is
 * shown (no `label` — « sans transformation »), green when it starts with
 * « Payée » (`Payée client`…), red with « Impayée », neutral otherwise
 * (`Ristourne à payer`…). Shared by the contract « Primes » tab and the
 * « Mes primes » list so both colour the same values alike.
 */
export const PREMIUM_SITUATION_BADGE: StatusBadgeConfig = {
  entries: {
    Payée: { variant: 'success' },
    Impayée: { variant: 'error' },
  },
  match: 'prefix',
  fallbackVariant: 'default',
};
